import {
	ReactElement,
	ReadonlyRefObject,
	useCallback,
	useMemo,
	useRef,
	useState,
	KeyboardEvent
} from "react";
import { ButtonBase } from "../ButtonBase/ButtonBase";
import { localize } from "client_legacy/ndaw/culture";
import {
	EmojiPickerGroup,
	emojiPickerGroupTitleMap,
	EmojiPickerTabs,
	EmojiTab
} from "./EmojiPickerTabs";
import { Guid, newGuid } from "client_legacy/ndaw/guid";
import { NoResults } from "../NoResults/NoResults";
import { never } from "client_legacy/ndaw/errors";
import { SearchInput, SearchInputHandle } from "../SearchInput/SearchInput";
import {
	delay,
	generateTrackingId,
	isNil,
	isNotNilOrEmpty
} from "client/Misc/utils";
import { Key } from "client/Misc/commonTypes";
import { SkinTone, SkinToneSelector } from "./SkinToneSelector";
import {
	Emoji,
	EmojiGroup,
	EmojisWithGroup,
	useEmojiPickerData
} from "./useEmojiPickerData";
import { useRecentEmojis } from "./useRecentEmojis";
import styles from "./EmojiPicker.module.less";
import { BusyIndicator } from "../BusyIndicator/BusyIndicator";
import {
	contains,
	NormalizedSearchTerm,
	normalizeForSearch
} from "client/Data/searching";
import {
	KeyboardGridNavigationContext,
	KeyboardGridNavigationContextData,
	useKeyboardGridNavigation,
	useKeyboardGridNavigationItem,
	useLocalStorageState,
	useUserScopedKey,
	useFocusTrap,
	useIsTouchDevice,
	GridNavigationDirection
} from "client/Hooks";
import { DropOverMenu } from "../DropOverMenu/DropOverMenu";
import { tryResolveKeyboardKey } from "client/Misc/keyboardUtils";
import { FormattedText } from "../FormattedText/FormattedText";
import { Typography } from "../Typography/Typography";

export type EmojiPickerProps = {
	target: HTMLElement;
	onClose: () => void;
	onSelected: (emojiUnicode: string) => void;
	trackingId: string;
};

export const EmojiPicker = (props: EmojiPickerProps): ReactElement => {
	const {
		target,
		onClose,
		onSelected,
		trackingId
	} = props;

	const {
		state,
		reload
	} = useEmojiPickerData();

	const selectEmojiHandler = useCallback(
		(emojiUnicode: string): void => {
			onSelected(emojiUnicode);
			onClose();
		},
		[onSelected, onClose]
	);

	const renderContent = (): ReactElement => {
		switch (state.kind) {
			case "loading":
				return (
					<BusyIndicator />
				);
			case "error":
				return (
					<NoResults
						icon="gear"
						title={localize("_UnableToRetrieveItems")}
						message={localize("_WeWereUnableToRetrieveTheItems")}
						onRetry={reload}
						height="fill"
					/>
				);
			case "success":
				return (
					<Content
						data={state.data}
						onSelectEmoji={selectEmojiHandler}
					/>
				);
			default:
				never(state);
		}
	};

	const keyDownHandler = useCallback(
		(args: KeyboardEvent<HTMLDivElement>): void => {
			const key: Key | null = tryResolveKeyboardKey(args);

			if (key === Key.Escape) {
				target.focus();
				onClose();
			}
		},
		[onClose, target]
	);

	return (
		<DropOverMenu
			target={target}
			onClose={onClose}
			title={localize("_Emoji")}
			trackingId={generateTrackingId(trackingId, "__wrapper")}
			hasNoPadding
		>
			<div
				className={styles.root}
				onKeyDown={keyDownHandler}
			>
				{renderContent()}
			</div>
		</DropOverMenu>
	);
};
EmojiPicker.displayName = "EmojiPicker";

type ContentProps = {
	data: readonly EmojisWithGroup[];
	onSelectEmoji: (emojiUnicode: string) => void;
};

const Content = (props: ContentProps): ReactElement => {
	const {
		data,
		onSelectEmoji
	} = props;

	const [searchTerm, setSearchTerm] = useState<VersionedValue<string>>({
		value: "",
		version: newGuid(),
	});

	const skinToneStorageKey: string = useUserScopedKey("emoji-skin-tone");
	const [skinTone, setSkinTone] = useLocalStorageState<SkinTone | null>(skinToneStorageKey, null);

	const [tabbableEmojiIndex, setTabbableEmojiIndex] = useState<number>(0);
	const [scrollTop, setScrollTop] = useState<number>(0);
	const isTouch: boolean = useIsTouchDevice();
	const scrollContainerRef: ReadonlyRefObject<HTMLDivElement | null> = useRef<HTMLDivElement | null>(null);
	const groupElementRefs: ReadonlyRefObject<Map<EmojiPickerGroup, HTMLElement>> = useRef<Map<EmojiPickerGroup, HTMLElement>>(new Map());
	const containerRef: ReadonlyRefObject<HTMLDivElement | null> = useRef<HTMLDivElement | null>(null);
	const searchInputRef: ReadonlyRefObject<SearchInputHandle | null> = useRef<SearchInputHandle | null>(null);

	useFocusTrap<HTMLDivElement>(containerRef);

	const {
		recentEmojis,
		registerRecentEmoji
	} = useRecentEmojis(data);

	const tabs = useMemo(
		(): readonly EmojiTab[] => {
			const groups: EmojiTab[] = [];

			if (recentEmojis.length > 0) {
				groups.push(EmojiPickerGroup.Recent);
			}

			for (const group of data) {
				groups.push(
					convertEmojiGroupToEmojiTab(group.group)
				);
			}

			return groups;
		},
		[data, recentEmojis]
	);

	const hasSearchTerm: boolean = isNotNilOrEmpty(searchTerm.value);

	const setGroupElementRef = useCallback(
		(group: EmojiPickerGroup, element: HTMLElement | null): void => {
			if (isNil(element)) {
				groupElementRefs.current
					.delete(group);
				return;
			}

			groupElementRefs.current
				.set(group, element);
		},
		[]
	);

	const onScrollHandler = useCallback(
		(): void => {
			setScrollTop(
				scrollContainerRef.current
					?.scrollTop ?? 0
			);
		},
		[]
	);

	const scrollToGroup = useCallback(
		(group: EmojiPickerGroup): void => {
			groupElementRefs.current
				.get(group)
				?.scrollIntoView({
					behavior: "smooth"
				});
		},
		[]
	);

	const tabClickHandler = useCallback(
		async (tab: EmojiTab): Promise<void> => {
			if (hasSearchTerm) {
				setSearchTerm({
					value: "",
					version: newGuid(),
				});

				// Wait for search to clear, then scroll to the right group.
				await delay(1);
			}

			scrollToGroup(tab);
		},
		[hasSearchTerm, scrollToGroup]
	);

	const searchHandler = useCallback(
		(value: string, version: Guid): void => {
			setSearchTerm({ value, version });

			setTabbableEmojiIndex(0);
			scrollContainerRef.current
				?.scrollTo({
					top: 0,
					behavior: "smooth"
				});
		},
		[]
	);

	const selectEmojiHandler = useCallback(
		async (emojiUnicode: string): Promise<void> => {
			registerRecentEmoji(emojiUnicode);

			// onSelectEmoji will unmount the component, useLocalStorageState uses a setState callback that get scheduled
			// When a component is unmounted, the scheduled callback is not executed
			await delay(1);
			onSelectEmoji(emojiUnicode);
		},
		[onSelectEmoji, registerRecentEmoji]
	);

	const activeTab = useMemo(
		(): EmojiTab | null => {
			if (hasSearchTerm) {
				return null;
			}

			const scrollContainer: HTMLDivElement | null = scrollContainerRef.current;

			if (isNil(scrollContainer)) {
				return tabs[0] ?? null;
			}

			const scrollContainerTop: number = scrollContainer.getBoundingClientRect()
				.top;

			const tabFromScroll: EmojiTab | undefined = tabs.findLast(
				group => {
					const groupElement: HTMLElement | undefined = groupElementRefs.current
						.get(group);

					if (isNil(groupElement)) {
						return false;
					}

					const groupOffsetTop: number = scrollTop
						+ groupElement.getBoundingClientRect().top
						- scrollContainerTop;

					return groupOffsetTop <= scrollTop;
				}
			);

			return tabFromScroll ?? tabs[0];
		},
		[
			tabs,
			hasSearchTerm,
			scrollTop
		]
	);

	const results = useMemo(
		(): readonly EmojiPickerData[] => {
			if (isNotNilOrEmpty(searchTerm.value)) {
				const normalizedSearchTerm: NormalizedSearchTerm = normalizeForSearch(searchTerm.value);

				const emojis: readonly Emoji[] = data.reduce(
					(accumulatedEmojis: Emoji[], group: EmojisWithGroup): Emoji[] => {
						group.emojis
							.forEach(
								(emoji: Emoji): void => {
									if (
										contains(emoji.label, normalizedSearchTerm)
										|| emoji.tags
											?.some(tag => contains(tag, normalizedSearchTerm))
									) {
										accumulatedEmojis.push(emoji);
									}
								}
							);

						return accumulatedEmojis;
					},
					[]
				);

				if (emojis.length === 0) {
					return [];
				}

				return [{
					group: EmojiPickerGroup.Results,
					emojis
				}];
			}

			const results: EmojiPickerData[] = [];

			if (recentEmojis.length > 0) {
				results.push({
					group: EmojiPickerGroup.Recent,
					emojis: recentEmojis,
				});
			}

			data.forEach(
				(group: EmojisWithGroup): void => {
					results.push({
						group: convertEmojiGroupToEmojiTab(group.group),
						emojis: group.emojis,
					});
				}
			);

			return results;
		},
		[
			searchTerm.value,
			recentEmojis,
			data
		]
	);

	const gridOutOfBoundsHandler = useCallback(
		(direction: GridNavigationDirection): void => {
			if (direction === Key.ArrowLeft || direction === Key.ArrowUp) {
				searchInputRef.current
					?.focus();
			}
		},
		[]
	);

	const {
		contextData: gridNavigationContextData,
		focusItemAtHandler
	} = useKeyboardGridNavigation({
		onFocusChange: setTabbableEmojiIndex,
		onOutOfBounds: gridOutOfBoundsHandler
	});

	const navigateToResultsHandler = useCallback(
		(): void => {
			focusItemAtHandler("start");
		},
		[focusItemAtHandler]
	);

	return (
		<div
			className={styles.content}
			ref={containerRef}
		>
			<div className={styles.tabsContainer}>
				<EmojiPickerTabs
					groups={tabs}
					activeGroup={activeTab}
					onTabClick={tabClickHandler}
				/>
			</div>
			<div className={styles.searchAndSkinTone}>
				<div className={styles.search}>
					<SearchInput
						placeholder={
							<FormattedText
								text={localize("_FilterItemName")}
								thingsToReplace={[
									{
										what: "{{ItemName}}",
										filler: (
											<Typography weight="bold" isItalic>
												{localize("_Emojis")}
											</Typography>
										)
									}
								]}
							/>
						}
						onChange={searchHandler}
						value={searchTerm}
						icon="filter"
						autoFocus={!isTouch}
						onNavigateToResults={navigateToResultsHandler}
						ref={searchInputRef}
					/>
				</div>
				<div className={styles.skinTone}>
					<SkinToneSelector
						value={skinTone}
						onChange={setSkinTone}
					/>
				</div>
			</div>
			<div
				className={styles.emojiResultContainer}
				ref={scrollContainerRef}
				onScroll={onScrollHandler}
			>
				<EmojiResult
					results={results}
					onSelectEmoji={selectEmojiHandler}
					onGroupElementRef={setGroupElementRef}
					tabbableIndex={tabbableEmojiIndex}
					onTabbableIndexChange={setTabbableEmojiIndex}
					gridNavigationContextData={gridNavigationContextData}
					skinTone={skinTone}
				/>
			</div>
		</div>
	);
};
Content.displayName = "Content";

type EmojiResultProps = {
	results: readonly EmojiPickerData[];
	onSelectEmoji: (emojiUnicode: string) => void;
	onGroupElementRef: (group: EmojiPickerGroup, element: HTMLElement | null) => void;
	tabbableIndex: number;
	onTabbableIndexChange: (index: number) => void;
	gridNavigationContextData: KeyboardGridNavigationContextData;
	skinTone: SkinTone | null;
};

const EmojiResult = (props: EmojiResultProps): ReactElement => {
	const {
		results,
		onSelectEmoji,
		onGroupElementRef,
		tabbableIndex,
		onTabbableIndexChange,
		gridNavigationContextData,
		skinTone
	} = props;

	if (results.length === 0) {
		return (
			<NoResults
				icon="magnifying-glass"
				title={localize("_NoSearchResults")}
				message={localize("_PleaseTrySearchingWithAnotherTerm")}
				height="fill"
			/>
		);
	}

	let emojiIndex = 0;

	return (
		<KeyboardGridNavigationContext.Provider value={gridNavigationContextData}>
			<ul className={styles.emojiGroupList}>
				{results.map(result => (
					<li
						key={result.group}
						ref={(element): void => onGroupElementRef(result.group, element)}

					>
						<section>
							<p className={styles.groupTitle}>
								{localize(emojiPickerGroupTitleMap[result.group])}
							</p>
							<ul className={styles.emojiGrid}>
								{result.emojis
									.map(
										(emoji: Emoji): ReactElement => {
											const currentEmojiIndex: number = emojiIndex;
											emojiIndex++;

											return (
												<li key={emoji.unicode}>
													<EmojiButton
														skinTone={skinTone}
														index={currentEmojiIndex}
														emoji={emoji}
														onSelectEmoji={onSelectEmoji}
														onFocus={onTabbableIndexChange}
														tabIndex={
															tabbableIndex === currentEmojiIndex
																? 0
																: -1
														}
													/>
												</li>
											);
										}
									)
								}
							</ul>
						</section>
					</li>
				))}
			</ul>
		</KeyboardGridNavigationContext.Provider>
	);
};
EmojiResult.displayName = "EmojiResult";

type EmojiButtonProps = {
	skinTone: SkinTone | null;
	index: number;
	emoji: Emoji;
	onSelectEmoji: (emojiUnicode: string) => void;
	onFocus: (index: number) => void;
	tabIndex: number;
};

type EmojiPickerData = {
	group: EmojiPickerGroup;
	emojis: readonly Emoji[];
};

const EmojiButton = (props: EmojiButtonProps): ReactElement => {
	const {
		skinTone,
		index,
		emoji,
		onSelectEmoji,
		onFocus,
		tabIndex
	} = props;

	const emojiValue = useMemo(
		(): string => {
			if (isNil(skinTone)) {
				return emoji.unicode;
			}

			const skinIndex: number = skinToneToEmojiIndex[skinTone];

			return emoji.skins
				?.[skinIndex]
				?? emoji.unicode;
		},
		[emoji, skinTone]
	);

	const {
		ref,
		onKeyDown
	} = useKeyboardGridNavigationItem({
		index,
		onExecute: (): void => onSelectEmoji(emojiValue),
		executeKey: [Key.Enter, Key.Space]
	});

	return (
		<ButtonBase
			ref={ref}
			className={styles.emojiButton}
			title={emoji.label}
			onClick={(): void => onSelectEmoji(emojiValue)}
			onKeyDown={onKeyDown}
			onFocus={(): void => onFocus(index)}
			tabIndex={tabIndex}
		>
			{emojiValue}
		</ButtonBase>
	);
};
EmojiButton.displayName = "EmojiButton";

const convertEmojiGroupToEmojiTab = (group: EmojiGroup): EmojiTab => {
	switch (group) {
		case EmojiGroup.SmileysAndEmotion:
			return EmojiPickerGroup.SmileysAndEmotion;
		case EmojiGroup.PeopleAndBody:
			return EmojiPickerGroup.PeopleAndBody;
		case EmojiGroup.AnimalsAndNature:
			return EmojiPickerGroup.AnimalsAndNature;
		case EmojiGroup.FoodAndDrink:
			return EmojiPickerGroup.FoodAndDrink;
		case EmojiGroup.TravelAndPlaces:
			return EmojiPickerGroup.TravelAndPlaces;
		case EmojiGroup.Activities:
			return EmojiPickerGroup.Activities;
		case EmojiGroup.Objects:
			return EmojiPickerGroup.Objects;
		case EmojiGroup.Symbols:
			return EmojiPickerGroup.Symbols;
		case EmojiGroup.Flags:
			return EmojiPickerGroup.Flags;
		default:
			never(group);
	}
};

const skinToneToEmojiIndex: Record<SkinTone, number> = {
	[SkinTone.Light]: 0,
	[SkinTone.MediumLight]: 1,
	[SkinTone.Medium]: 2,
	[SkinTone.MediumDark]: 3,
	[SkinTone.Dark]: 4
};